/**
 * Browser events that qualify as user interaction with the application.
 *
 * Interaction is intentionally broad. The goal is not to determine what the
 * user intended to do, but simply to know that the user is actively engaging
 * with the page.
 *
 * Once any one of these events occurs, the application is considered
 * interacted with for the remainder of the current Nuxt app lifecycle.
 */
const INTERACTION_EVENTS = [
  'mousemove',
  'pointerdown',
  'keydown',
  'scroll',
] as const satisfies readonly (keyof WindowEventMap)[];

/**
 * Unique Nuxt state key used to persist interaction state across components
 * and client-side navigations.
 *
 * `useState` is used rather than a module-level `ref` because Nuxt state is
 * SSR-safe and shared between composable consumers.
 */
const INTERACTION_STATE_KEY = 'user_interacted';

/**
 * Callback invoked once the user has interacted with the application.
 *
 * The return value is intentionally ignored. This allows both synchronous
 * and asynchronous callbacks without requiring callers to wrap them.
 */
type InteractionCallback = () => unknown;

/**
 * Cleanup function for the currently registered global interaction listeners.
 *
 * This is module-scoped because interaction detection belongs to the browser
 * application as a whole rather than to any individual Vue component.
 *
 * Having a single cleanup function also prevents multiple composable
 * consumers from registering duplicate sets of window event listeners.
 */
let stopInteractionTracking: (() => void) | undefined;

/**
 * Ensures that global browser interaction listeners have been registered.
 *
 * Only one set of listeners is registered, regardless of how many components
 * call `useUserInteraction()` or `onInteraction()`.
 *
 * Tracking is not started when:
 *
 * - running during SSR;
 * - the user has already interacted; or
 * - another consumer has already initialized the listeners.
 *
 * The listeners intentionally outlive the Vue component that first caused
 * them to be registered. Interaction state is application-wide, so destroying
 * that component should not stop interaction detection for the rest of the
 * application.
 *
 * @param interacted Shared mutable interaction state.
 */
const ensureInteractionTracking = (interacted: Ref<boolean>): void => {
  if (
    import.meta.server
      || interacted.value
      || stopInteractionTracking
  ) {
    return;
  }

  /**
   * Removes every registered interaction listener and marks the global
   * tracker as inactive.
   *
   * `removeEventListener` does not need the original `passive` option.
   * Listener identity is determined by the event type, callback, and capture
   * value. Capture defaults to `false` for both registration and removal.
   */
  const cleanup = () => {
    for (const event of INTERACTION_EVENTS) {
      window.removeEventListener(event, handleInteraction);
    }

    stopInteractionTracking = undefined;
  };

  /**
   * Handles the application's first observed user interaction.
   *
   * Interaction state is monotonic: once it becomes `true`, it is never reset
   * during the current app lifecycle.
   *
   * Updating the shared ref notifies every subscriber before the global
   * listeners are discarded.
   */
  const handleInteraction = () => {
    interacted.value = true;
    cleanup();
  };

  /**
   * Store the cleanup function before registering listeners so subsequent
   * composable calls know that tracking has already been initialized.
   */
  stopInteractionTracking = cleanup;

  for (const event of INTERACTION_EVENTS) {
    window.addEventListener(event, handleInteraction, {
      /**
       * None of the interaction handlers call `preventDefault()`, so the
       * listeners can safely be passive.
       */
      passive: true,
    });
  }

  /**
   * Remove listeners during Vite HMR.
   *
   * This prevents duplicate listeners from surviving a module replacement
   * while developing the application. It has no effect in production.
   */
  import.meta.hot?.dispose(cleanup);
};

/**
 * Returns application-wide user interaction state.
 *
 * The returned ref becomes `true` after the first qualifying browser
 * interaction and remains `true` for the remainder of the current Nuxt app
 * lifecycle.
 *
 * Because the state is backed by Nuxt `useState`, all components and
 * composables observe the same value. A component mounted after interaction
 * therefore immediately sees `true`.
 *
 * On the server the state remains `false`; browser listeners are only
 * registered on the client.
 *
 * @returns A readonly ref indicating whether the user has interacted.
 *
 * @example
 * ```ts
 * const interacted = useUserInteraction();
 *
 * if (interacted.value) {
 *   // The user has already interacted with the application.
 * }
 * ```
 */
export const useUserInteraction = (): Readonly<Ref<boolean>> => {
  const interacted = useState<boolean>(
    INTERACTION_STATE_KEY,
    () => false,
  );

  ensureInteractionTracking(interacted);

  return readonly(interacted);
};

/**
 * Runs a callback once the user has interacted with the application.
 *
 * This helper supports both early and late subscribers:
 *
 * - If the user has not interacted yet, the callback waits for the first
 *   interaction.
 * - If the user has already interacted, the callback runs immediately.
 *
 * Every caller has its own subscription. Multiple consumers can therefore
 * independently register callbacks without racing against one another.
 *
 * The interaction itself is detected globally by `useUserInteraction()`;
 * this function does not register additional browser event listeners.
 *
 * When waiting for future interaction, Vue's `watch(..., { once: true })`
 * automatically stops the watcher after the interaction state changes.
 *
 * When called from a Vue setup scope, the watcher is also automatically tied
 * to that scope. If the component is destroyed before interaction occurs,
 * its callback will no longer run, while global interaction tracking
 * continues for the rest of the application.
 *
 * @param callback Optional callback to invoke after user interaction.
 *
 * @returns A readonly ref indicating whether the user has interacted.
 *
 * @example
 * ```ts
 * onInteraction(() => {
 *   void loadRecommendations();
 * });
 * ```
 *
 * @example Late subscriber
 * ```ts
 * // If interaction already happened before this component mounted,
 * // `loadGallery()` runs immediately.
 * onInteraction(() => {
 *   void loadGallery();
 * });
 * ```
 *
 * @example Reactive usage
 * ```ts
 * const interacted = onInteraction();
 *
 * // `interacted.value` is shared across the entire application.
 * ```
 */
export const onInteraction = (
  callback?: InteractionCallback,
): Readonly<Ref<boolean>> => {
  const interacted = useUserInteraction();

  /**
   * Callbacks represent client-side reactions to browser interaction and
   * should never execute during SSR.
   */
  if (import.meta.server || !callback) {
    return interacted;
  }

  /**
   * Late subscriber.
   *
   * Interaction has already happened, so there is nothing to wait for.
   * Execute synchronously rather than creating an unnecessary watcher.
   */
  if (interacted.value) {
    void callback();

    return interacted;
  }

  /**
   * Early subscriber.
   *
   * The shared state is currently false and is monotonic, so its first change
   * will be false -> true. `once: true` therefore gives us exactly the desired
   * behavior without needing manual watcher cleanup.
   */
  watch(
    interacted,
    () => {
      void callback();
    },
    {
      once: true,
    },
  );

  return interacted;
};
